"use client";

import Image from "next/image";
import Link from "next/link";
import { Suspense } from "react";
import { ArrowRight, Ruler, Eye, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { FAQSection } from "@/components/landing/faq-section";
import { LandingPricingSection } from "@/components/landing/landing-pricing-section";
import { useLandingCta } from "@/hooks/use-landing-cta";

const FITMENT_SHOWCASE = [
  {
    name: "Sedan Fitment",
    before: "/showcase/images/before1.jpg",
    imageClassName: "object-[center_70%]",
    wheels: [
      {
        id: "fit-1",
        name: "Conservative Fit",
        image: "/showcase/images/after11.webp",
        thumbnail: "/showcase/images/after11.webp",
      },
      {
        id: "fit-2",
        name: "Flush Fit",
        image: "/showcase/images/after12.webp",
        thumbnail: "/showcase/images/after12.webp",
      },
      {
        id: "fit-3",
        name: "Aggressive Fit",
        image: "/showcase/images/after13.webp",
        thumbnail: "/showcase/images/after13.webp",
      },
    ],
  },
  {
    name: "SUV Fitment",
    before: "/showcase/images/before4.jpg",
    wheels: [
      {
        id: "fit-4",
        name: "OEM+ Look",
        image: "/showcase/images/after41.webp",
        thumbnail: "/showcase/images/after41.webp",
      },
      {
        id: "fit-5",
        name: "Wide Body",
        image: "/showcase/images/after42.webp",
        thumbnail: "/showcase/images/after42.webp",
      },
    ],
  },
];

const FITMENT_CONCERNS = [
  {
    title: "Stance and poke",
    description:
      "See how aggressive offsets will look. Will the wheels sit flush, tuck, or poke? Preview before you commit.",
  },
  {
    title: "Wheel well clearance",
    description:
      "Visualize how larger wheels fill the wheel wells. Get a sense of the proportions without test-fitting.",
  },
  {
    title: "Overall aesthetics",
    description:
      "Some wheels look great in a catalog but wrong on certain cars. Preview the actual look on your car.",
  },
];

const FITMENT_FAQS = [
  {
    question: "Does this tell me exact fitment specs?",
    answer:
      "This tool shows you how wheels will LOOK on your car-the visual fitment. For technical specs like bolt pattern, hub bore, and offset clearance, always verify with your wheel retailer or use a fitment database. Our preview helps you decide if you like the look before diving into specs.",
  },
  {
    question: "Can I see different wheel sizes?",
    answer:
      "Yes. Upload wheels in different sizes (18\", 19\", 20\", etc.) and preview each on your car. See how sizing up or down changes the overall look.",
  },
  {
    question: "Will it show rubbing or scraping issues?",
    answer:
      "The preview shows visual appearance, not physical clearance. For technical fitment concerns like fender rubbing or suspension clearance, consult with a wheel specialist. Our tool helps you narrow down options visually first.",
  },
  {
    question: "Can I preview lowered cars?",
    answer:
      "Yes. If your car is already lowered, upload a photo of it as-is. The AI works with your car's actual current stance, whether stock, lowered, or lifted.",
  },
  {
    question: "What about staggered setups?",
    answer:
      "You can preview different wheels for front and rear. Run separate previews with each wheel to see how a staggered setup would look.",
  },
  {
    question: "Is this accurate for purchase decisions?",
    answer:
      "Many users use our previews to make purchase decisions. The visual representation is accurate enough to know if a wheel style suits your car. For final technical fitment, always confirm with your wheel shop.",
  },
];

export function WheelFitmentPreviewContent() {
  const { heroCtaRef, showStickyCta, ctaText, handleTryItNow } = useLandingCta();

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Wheel Fitment Preview",
    url: "https://thewheelvisualizer.com/wheel-fitment-preview",
    description:
      "Preview wheel fitment before buying. See how different wheels look on your car.",
    mainEntity: {
      "@type": "SoftwareApplication",
      name: "The Wheel Visualizer",
      operatingSystem: "Web",
      applicationCategory: "GraphicsApplication",
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to preview wheel fitment on your car",
    step: [
      {
        "@type": "HowToStep",
        name: "Upload your car photo",
        text: "Take a side profile or 3/4 angle photo of your car and upload it.",
      },
      {
        "@type": "HowToStep",
        name: "Select wheels to preview",
        text: "Choose from our library or upload wheel images you're considering.",
      },
      {
        "@type": "HowToStep",
        name: "Review fitment preview",
        text: "See how the wheels look on your car. Check stance, fill, and overall aesthetics.",
      },
    ],
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
                <Ruler className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/70">Visual fitment preview</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Wheel Fitment Preview
                <br />
                <span className="text-white/70">See Before You Buy</span>
              </h1>
              
              <p className="text-lg text-white/60 mb-4 leading-relaxed">
                Will these wheels look right on your car? Preview the fitment-stance, fill, and style-before making a purchase decision.
              </p>
              <p className="text-white/80 font-medium mb-8">
                Visual fitment preview for confident wheel buying.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" ref={heroCtaRef}>
                <Button
                  size="lg"
                  onClick={handleTryItNow}
                  className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
                >
                  {ctaText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-white/20 text-white hover:bg-white/5 h-12 px-8"
                >
                  <Link href="/credits">View Pricing</Link>
                </Button>
              </div>

              {/* Stats */}
              <div className="mt-10 flex flex-wrap gap-8">
                <div className="flex items-center gap-2">
                  <Ruler className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">Any Size</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">Realistic Preview</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">&lt;60s Results</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <Image
                  src="/showcase/images/after12.webp"
                  alt="Wheel fitment preview"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
              </div>
              <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl bg-[#0A0C0F] border border-white/10">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-white/60" />
                  <span className="text-sm text-white/80">Preview any wheel</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fitment concerns */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">What you can preview</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              See the look before you commit
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl">
              Fitment isn&apos;t just about whether wheels physically fit-it&apos;s about whether they look right. Preview these visual aspects before ordering.
            </p>
          </div>

          <div className="space-y-6">
            {FITMENT_CONCERNS.map((concern, idx) => (
              <div
                key={concern.title}
                className="flex gap-6 items-start p-6 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                <div className="text-4xl font-bold text-white/10 shrink-0 w-12">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{concern.title}</h3>
                  <p className="text-white/60 leading-relaxed">{concern.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important disclaimer */}
      <section className="py-12 px-6 bg-[#05070C] border-y border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
            <p className="text-white/80 leading-relaxed">
              <strong className="text-white">Note:</strong> This tool provides visual fitment previews-how wheels will look on your car. For technical fitment specifications (bolt pattern, offset, hub bore, clearance), always verify with your wheel retailer or mechanic before purchasing.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive showcase */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Preview examples</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Compare fitment options
            </h2>
            <p className="text-white/60 mt-4">
              Drag the slider to compare. Tap thumbnails to see different wheel styles.
            </p>
          </div>

          <div className="space-y-16">
            {FITMENT_SHOWCASE.map((scenario, idx) => (
              <div key={scenario.name}>
                <p className="text-sm text-white/50 mb-4">{scenario.name}</p>
                <InteractiveShowcase
                  beforeImage={scenario.before}
                  wheels={scenario.wheels}
                  imageClassName={scenario.imageClassName}
                  priority={idx === 0}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to use for fitment */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Workflow</p>
              <h2 className="text-3xl font-semibold text-white mb-6">
                Narrow down before you buy
              </h2>
              <p className="text-white/60 leading-relaxed mb-8">
                Instead of ordering wheels and hoping they look right, preview first. This workflow helps you make confident decisions:
              </p>
              <ol className="space-y-6">
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-sm font-medium text-white/70">
                    1
                  </div>
                  <div>
                    <div className="text-white font-medium mb-1">Browse wheels you like</div>
                    <p className="text-white/60 text-sm">Find wheels online-retailers, forums, Instagram-and save the images.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-sm font-medium text-white/70">
                    2
                  </div>
                  <div>
                    <div className="text-white font-medium mb-1">Preview each on your car</div>
                    <p className="text-white/60 text-sm">Upload your car photo and preview each wheel option. Compare side by side.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-sm font-medium text-white/70">
                    3
                  </div>
                  <div>
                    <div className="text-white font-medium mb-1">Confirm technical fitment</div>
                    <p className="text-white/60 text-sm">Once you&apos;ve chosen visually, verify specs with your wheel shop or fitment database.</p>
                  </div>
                </li>
              </ol>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
              <Image
                src="/showcase/after1.webp"
                alt="Fitment preview result"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#04060A]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Fitment preview questions
            </h2>
            <p className="text-white/60 mt-4">
              Common questions about using this tool for fitment decisions.
            </p>
          </div>
          <FAQSection faqs={FITMENT_FAQS} />
        </div>
      </section>

      <Suspense fallback={<div className="py-24 px-6 bg-[#0A0C0F]" />}>
        <LandingPricingSection />
      </Suspense>

      {/* Sticky CTA */}
      {showStickyCta && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#05070C]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3">
            <button
              onClick={handleTryItNow}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white text-black font-semibold"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
